import { getActingCustomer } from '@/lib/actingContext';
import { supabase } from '@/lib/supabase';

/**
 * Client helpers for the customer-facing branded documents (invoice +
 * statement). Download AND Print both use the SERVER pdf-lib renderers
 * (`customer-invoice-pdf` / `customer-statement-pdf`) — the exact same
 * documents the DocuPost mailed paper uses — so every channel (mailed,
 * downloaded, printed) emits the identical PDF. The on-screen HTML document
 * stays as the in-app view only.
 *
 * Print and View open the PDF in a tab that is opened at the click, before
 * the fetch (see openPdfTab). Download saves the file.
 *
 * (Until 2026-07-09 Download captured the on-screen DOM with html2canvas and
 * Print used window.print() — three different-looking outputs. Sky picked the
 * mailed/paper format as canonical.)
 */

/** Statuses worth one retry: a cold start or a platform timeout, not a refusal. */
const RETRYABLE = new Set([502, 503, 504]);

/** Fetch a function endpoint that returns application/pdf, as a Blob.
 *  Retries ONCE on a network error or a 502/503/504 — the usual cause of a
 *  PDF that "sometimes" fails is a cold function, and a second try a moment
 *  later almost always lands. A 4xx is never retried: it is an answer. */
export async function fetchFunctionPdf(path: string): Promise<Blob> {
  let lastErr: unknown;
  for (let attempt = 0; attempt < 2; attempt++) {
    if (attempt > 0) await new Promise((r) => setTimeout(r, 900));
    try {
      return await fetchFunctionPdfOnce(path);
    } catch (err) {
      lastErr = err;
      const status = (err as { status?: number }).status;
      const retryable = status === undefined ? (err as { network?: boolean }).network === true : RETRYABLE.has(status);
      if (!retryable) break;
    }
  }
  throw lastErr;
}

async function fetchFunctionPdfOnce(path: string): Promise<Blob> {
  const { data: sessionData } = await supabase.auth.getSession();
  const token = sessionData.session?.access_token;
  if (!token) throw new Error('Your session has ended — sign in again to get this PDF.');

  // Same acting-customer stamp adminFetch sends — a superadmin with two tabs
  // open as two customers must not print the other tab's document.
  const acting = getActingCustomer();
  let res: Response;
  try {
    res = await fetch(`/.netlify/functions/${path}`, {
      headers: {
        Authorization: `Bearer ${token}`,
        ...(acting ? { 'x-acting-customer': acting } : {}),
      },
    });
  } catch {
    throw Object.assign(new Error('Could not reach the server to build the PDF.'), { network: true });
  }
  if (!res.ok) {
    const text = await res.text();
    let msg = `HTTP ${res.status}`;
    try {
      const parsed = JSON.parse(text) as { error?: string };
      if (parsed.error) msg = parsed.error;
    } catch {
      // A platform timeout returns an HTML page, not our JSON — say what it means.
      msg = RETRYABLE.has(res.status)
        ? 'The PDF took too long to build. Please try again.'
        : text ? `${msg}: ${text.slice(0, 160)}` : msg;
    }
    throw Object.assign(new Error(msg), { status: res.status });
  }
  const blob = await res.blob();
  if (!blob.size) throw Object.assign(new Error('The PDF came back empty. Please try again.'), { status: 502 });
  return blob.type === 'application/pdf' ? blob : new Blob([blob], { type: 'application/pdf' });
}

/** Trigger a browser download of a PDF blob. */
export function savePdfBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Give the download a moment to start before revoking.
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

/**
 * Open a tab for a PDF NOW, while the click still counts as the user's.
 *
 * ⚠ This is the fix for "printing sometimes fails". A browser only lets a
 * page open a tab in direct response to a click, and that permission runs
 * out a few seconds later. The old flow fetched the PDF first and opened the
 * tab afterwards, so whenever the PDF was slow to build the tab was blocked
 * silently and nothing happened. Call this BEFORE the first await.
 */
export function openPdfTab(): Window | null {
  let w: Window | null = null;
  try {
    w = window.open('', '_blank');
  } catch {
    w = null;
  }
  if (w) {
    try {
      w.document.title = 'Preparing your PDF…';
      w.document.body.innerHTML =
        '<p style="font:16px system-ui,sans-serif;color:#334155;padding:48px;text-align:center">Preparing your PDF…</p>';
    } catch {
      /* cross-origin or locked down — the PDF still loads into it */
    }
  }
  return w;
}

/** Put an error message in a tab opened by openPdfTab, rather than leaving it blank. */
export function failPdfTab(tab: Window | null, message: string): void {
  if (!tab || tab.closed) return;
  try {
    tab.document.title = 'PDF unavailable';
    const p = tab.document.createElement('p');
    p.style.cssText = 'font:16px system-ui,sans-serif;color:#b91c1c;padding:48px;text-align:center';
    p.textContent = `We couldn't build this PDF: ${message} Close this tab and try again.`;
    tab.document.body.replaceChildren(p);
  } catch {
    tab.close();
  }
}

/**
 * Show a PDF in a tab from openPdfTab and, when asked, start printing it.
 * The browser's PDF viewer has its own Print and Save buttons, so if the
 * print dialog can't be started automatically the user prints from there.
 * With no tab (pop-ups blocked) the PDF is downloaded instead, so the click
 * always produces the document.
 */
export function showPdfInTab(
  tab: Window | null,
  blob: Blob,
  opts: { print?: boolean; filename: string },
): 'tab' | 'downloaded' {
  if (!tab || tab.closed) {
    savePdfBlob(blob, opts.filename);
    return 'downloaded';
  }
  const url = URL.createObjectURL(blob);
  tab.location.href = url;
  if (opts.print) {
    const started = Date.now();
    const tryPrint = () => {
      if (tab.closed || Date.now() - started > 15_000) return;
      try {
        if (tab.document.readyState === 'complete' && tab.location.href === url) {
          tab.focus();
          tab.print();
          return;
        }
      } catch {
        return; // the viewer is not scriptable here — its own Print button works
      }
      setTimeout(tryPrint, 300);
    };
    setTimeout(tryPrint, 600);
  }
  // Long enough for the viewer's own Save / Print to keep working.
  setTimeout(() => URL.revokeObjectURL(url), 10 * 60_000);
  return 'tab';
}

/**
 * One call for a PDF button. Opens the tab first (for view/print), then
 * fetches, then shows or downloads. Must be called synchronously from the
 * click handler — before any await — or the tab can be blocked.
 */
export async function runPdf(
  mode: 'view' | 'print' | 'download',
  load: () => Promise<Blob>,
  filename: string,
): Promise<void> {
  const tab = mode === 'download' ? null : openPdfTab();
  try {
    const blob = await load();
    if (mode === 'download') savePdfBlob(blob, filename);
    else showPdfInTab(tab, blob, { print: mode === 'print', filename });
  } catch (err) {
    failPdfTab(tab, err instanceof Error ? err.message : String(err));
    throw err;
  }
}
